import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrderData } from 'hooks/orderForms/useOrderData';
import { logError } from 'services/logging/logging';
import {
  useCreatePartnerParty,
  usePartnerParties,
  useUpdatePartnerParty,
} from 'services/repositories/partners/partners';
import { ConflictError, GenericError } from 'support/helpers/errors/errors';
import { OrderData } from 'support/helpers/orderForms/orderForms';
import { getPartyWithGS1orVAT } from 'support/helpers/parties/parties';
import { OrderFormDTO, PartyDTO, PartyIdentifierDTO, UserDTO } from 'support/types';

type UsePartySelectionProps = {
  supplierSlug: string;
  buyerParties: Array<PartyDTO> | undefined;
  orderFormData: OrderFormDTO | undefined;
  buyerId: string | undefined;
  currentUser: UserDTO | undefined;
  customerIdentifier: PartyIdentifierDTO | undefined;
};

const usePartyState = (orderData: OrderData) => {
  const initialBillingPartyId = useMemo(() => orderData.parties?.buyer?.id || null, [orderData]);
  const initialDeliveryPartyId = useMemo(() => orderData.parties?.ship_to?.id || null, [orderData]);
  const customerIdentifier = useMemo(
    () =>
      orderData.parties?.bill_to?.identifiers?.find(
        (identifier: PartyIdentifierDTO): boolean => identifier.domain === 'internal',
      ) || null,
    [orderData],
  );

  const [selectedBillingPartyId, setSelectedBillingPartyId] = useState<string | null>(initialBillingPartyId);
  const [selectedDeliveryPartyId, setSelectedDeliveryPartyId] = useState<string | null>(initialDeliveryPartyId);

  useEffect(() => {
    if (initialBillingPartyId && !selectedBillingPartyId) {
      setSelectedBillingPartyId(initialBillingPartyId);
    }
  }, [initialBillingPartyId, selectedBillingPartyId]);

  useEffect(() => {
    if (initialDeliveryPartyId && !selectedDeliveryPartyId) {
      setSelectedDeliveryPartyId(initialDeliveryPartyId);
    }
  }, [initialDeliveryPartyId, selectedDeliveryPartyId]);

  return {
    selectedBillingPartyId,
    setSelectedBillingPartyId,
    selectedDeliveryPartyId,
    setSelectedDeliveryPartyId,
    customerIdentifier,
  };
};

type PartyUpdatesProps = {
  selectedBillingPartyId: string | null;
  selectedDeliveryPartyId: string | null;
  buyerParties: Array<PartyDTO> | undefined;
  orderFormData: OrderFormDTO | undefined;
  setOrderData: React.Dispatch<React.SetStateAction<OrderData>>;
  currentUser: UserDTO | undefined;
  customerIdentifier: PartyIdentifierDTO | undefined;
};

/**
 * Updates the order data's parties whenever the selected billing or delivery party changes.
 */
const usePartyUpdates = ({
  selectedBillingPartyId,
  selectedDeliveryPartyId,
  buyerParties,
  orderFormData,
  setOrderData,
  currentUser,
  customerIdentifier,
}: PartyUpdatesProps) => {
  useEffect(() => {
    if (!buyerParties || !orderFormData?.data) {
      return;
    }

    const billingParty = selectedBillingPartyId
      ? buyerParties.find((party: PartyDTO) => party.id === selectedBillingPartyId)
      : null;

    if (
      billingParty &&
      customerIdentifier &&
      !billingParty.identifiers?.find(
        (identifier) => identifier.domain === 'internal' && identifier.identifier === customerIdentifier.identifier,
      )
    ) {
      billingParty.identifiers?.push(customerIdentifier);
    }

    const deliveryParty = selectedDeliveryPartyId
      ? buyerParties.find((party: PartyDTO) => party.id === selectedDeliveryPartyId)
      : null;

    const customerName = orderFormData.data?.customer.name;
    if (!customerName) {
      return;
    }

    setOrderData((current) => ({
      ...current,
      parties: {
        supplier: current.parties?.supplier,
        buyer: billingParty
          ? {
              ...billingParty,
              name: customerName,
              contact: {
                first_name: currentUser?.name.split(' ')[0],
                email: currentUser?.email,
              },
            }
          : current.parties?.buyer,
        ship_to: deliveryParty ? { ...deliveryParty, name: customerName } : current.parties?.ship_to,
        bill_to: billingParty ? { ...billingParty, name: customerName } : current.parties?.bill_to,
      },
    }));
  }, [
    selectedBillingPartyId,
    selectedDeliveryPartyId,
    buyerParties,
    setOrderData,
    orderFormData,
    currentUser,
    customerIdentifier,
  ]);
};

type PartyHandlersProps = {
  buyerId: string | undefined;
};

const usePartyHandlers = ({ buyerId }: PartyHandlersProps) => {
  const { t } = useTranslation();
  const [partyError, setPartyError] = useState<string | null>(null);
  const { mutate: createParty, isLoading: isCreating } = useCreatePartnerParty();
  const { mutate: updateParty, isLoading: isUpdating } = useUpdatePartnerParty();

  const handleAddParty = (
    party: PartyDTO,
    _partyType: 'billing' | 'delivery',
    onAdded?: (newParty: PartyDTO) => void,
  ) => {
    if (!buyerId) {
      logError('Order Forms: No buyer ID available');
      return;
    }

    setPartyError(null);

    createParty(
      {
        variables: { partnerId: buyerId },
        payload: party,
      },
      {
        onSuccess: (newParty) => {
          setPartyError(null);
          onAdded?.(newParty);
        },
        onError: (error) => {
          if (error instanceof ConflictError) {
            setPartyError(t('orderForms:checkout.addressModal.errors.conflict'));
          } else {
            logError(new GenericError('Order Forms: unexpected error creating a party', error));
            setPartyError(t('orderForms:checkout.addressModal.errors.generic'));
          }
        },
      },
    );
  };

  const handleEditParty = (party: PartyDTO, onEdited?: () => void) => {
    if (!buyerId) {
      logError('Order Forms: No buyer ID available');
      return;
    }

    setPartyError(null);

    updateParty(
      {
        variables: { partnerId: buyerId, partyId: party.id! },
        payload: party,
      },
      {
        onSuccess: () => {
          setPartyError(null);
          onEdited?.();
        },
        onError: (error) => {
          if (error instanceof ConflictError) {
            setPartyError(t('orderForms:checkout.addressModal.errors.conflict'));
          } else {
            logError(new GenericError('Order Forms: unexpected error editing a party', error));
            setPartyError(t('orderForms:checkout.addressModal.errors.generic'));
          }
        },
      },
    );
  };

  return {
    handleAddParty,
    handleEditParty,
    partyError,
    clearPartyError: () => setPartyError(null),
    isSubmitting: isCreating || isUpdating,
  };
};

export const usePartySelection = ({
  supplierSlug,
  buyerParties,
  orderFormData,
  buyerId,
  currentUser,
  customerIdentifier,
}: UsePartySelectionProps) => {
  const [orderData, setOrderData] = useOrderData(supplierSlug);

  const partyState = usePartyState(orderData);
  useSupplierPartyInitialization({ orderFormData, supplierSlug });
  usePartyUpdates({ ...partyState, buyerParties, orderFormData, setOrderData, currentUser, customerIdentifier });
  const partyHandlers = usePartyHandlers({ buyerId });

  const handleAddParty = (party: PartyDTO, partyType: 'billing' | 'delivery', onAdded?: () => void) => {
    const onSuccess = (newParty: PartyDTO) => {
      if (!newParty.id) {
        logError('Order Forms: New party created has no ID');
        return;
      }
      if (partyType === 'billing') {
        partyState.setSelectedBillingPartyId(newParty.id);
      } else if (partyType === 'delivery') {
        partyState.setSelectedDeliveryPartyId(newParty.id);
      }
      onAdded?.();
    };

    partyHandlers.handleAddParty(party, partyType, onSuccess);
  };

  const handleEditParty = (party: PartyDTO, onEdited?: () => void) => {
    partyHandlers.handleEditParty(party, onEdited);
  };

  return {
    ...partyState,
    handleAddParty,
    handleEditParty,
    partyError: partyHandlers.partyError,
    clearPartyError: partyHandlers.clearPartyError,
    isSubmittingParty: partyHandlers.isSubmitting,
  };
};

type UseSupplierPartyInitializationProps = {
  orderFormData: OrderFormDTO | undefined;
  supplierSlug: string;
};

const useSupplierPartyInitialization = ({ orderFormData, supplierSlug }: UseSupplierPartyInitializationProps) => {
  const [, setOrderData] = useOrderData(supplierSlug);
  const { data: supplierParties, isLoading: isLoadingSupplierParties } = usePartnerParties({
    variables: {
      partnerId: orderFormData?.data?.supplier.id,
    },
  });

  useEffect(() => {
    if (!isLoadingSupplierParties && supplierParties) {
      setOrderData((current) => ({
        ...current,
        parties: {
          buyer: current.parties?.buyer,
          ship_to: current.parties?.ship_to,
          bill_to: current.parties?.bill_to,
          supplier: {
            ...(getPartyWithGS1orVAT(supplierParties) || {}),
            name: orderFormData?.data?.supplier.name || '',
          },
        },
      }));
    }
  }, [isLoadingSupplierParties, orderFormData?.data?.supplier.name, setOrderData, supplierParties]);

  return { isLoadingSupplierParties };
};
